// @ts-nocheck
import { interactivityRegistration } from "./Charta";
import { DomQuery, HtmlClassSwitch, WithClass, WithId, WithTagName } from "./Dom";
import { Enumeration } from "./Enumeration";
import { distributeEvent } from "./Event";
import { SynchronizationState } from "./WebApp.Entity.Store";
import { application } from "./WebApp.Instance";
import { LoadingIndicator } from "./WebApp.LoadingIndicator";
import { ApplicationMode } from "./WebApp.Store";
import { ToastMessage } from "./WebApp.ToastBox";
import { ControlMode, Labels } from "./WebSite.Page.Component";

export class NavigationEvent {
  constructor(uri) {
    this.uri = uri;
  }
}

export const PageMode = new Enumeration(["Online", "Offline"]);

export class PageComponent {
  constructor(query) {
    this.query = query;
  }

  initialize(query) {
    this.element = this.load(query);
  }

  load(query) {
    return this.query(query);
  }

  reload(parent, query) {
    this.replace(parent, this.load(query));
  }

  replace(parent, element) {
    interactivityRegistration.detach(this.element);
    parent.replaceChild(element, this.element);
    this.element = element;
    interactivityRegistration.attach(element);
  }
}

export class PageHandler {
  constructor(rootUri) {
    this.rootUri = rootUri;
    this.mode = PageMode.Online;
    this.guards = new Set();

    this.contents = new PageComponent((query) => { return query.getChild(WithTagName("MAIN")); });
    this.breadcrumbs = new PageComponent((query) => { return query.getChild(WithClass("Breadcrumbs")); });

    this.components = new Array();
    this.components.push(new PageComponent((query) => { return query.getChild(WithClass("Menu")); }));
    this.components.push(this.breadcrumbs);
    this.components.push(this.contents);
    this.components.push(new PageComponent((query) => { return query.getChild(WithClass("WebSiteToolbar")); }));
    this.components.push(new PageComponent((query) => { return query.getChild(WithClass("Bookmarks")); }));

    this.uri = window.location.href;
  }

  start() {
    this.initializePage();
    this.addListeners();

    window.history.replaceState(
      { url: window.location.href },
      null,
      window.location.href
    );
  }

  addListeners() {
    addEventListener(
      "popstate",
      (event) => {
        if (event.state) {
          this.reload(window.location.href);
          event.preventDefault();
        }
      }
    );

    window.addEventListener(
      "click",
      (event) => {
        const link = event.target.closest("a");

        if (link && !event.ctrlKey && link.target !== "_blank" && !link.hasAttribute("download") && link.getAttribute("href")?.startsWith(this.rootUri)) {
          event.preventDefault();
          this.navigate(link.getAttribute("href"));
        }
      }
    );
  }

  build() {
    this.loadingIndicator = new LoadingIndicator();
    this.loading = new HtmlClassSwitch(this.element, "Loading");

    this.loadingPage = document.createElement("div");
    this.loadingPage.classList.add("LoadingPage");
    this.loadingPage.appendChild(this.loadingIndicator.element);

    this.element.appendChild(this.loadingPage);
  }

  initialize() {
    this.labels = new Labels(new DomQuery(document.body).getChild(WithClass("Labels")));
    this.element = document.getElementById("main");

    const query = new DomQuery(this.element);

    for (const component of this.components)
      component.initialize(query);

    this.build();
  }

  async initializePage() {
    this.setStatus(true);

    try {
      await this.loadOfflinePage(window.location.href, true);
    }
    finally {
      this.setStatus(false);
    }
  }

  handleDocument(uri, text) {
    this.guards.clear();
    const newDocument = new DOMParser().parseFromString(text, "text/html");

    if (this.loadSupported() && !this.reloadRequired(newDocument)) {
      const main = new DomQuery(newDocument).getDescendant(WithId("main"));
      const query = new DomQuery(main);

      if (!this.components.some((component) => component.load(query) === null)) {
        document.title = newDocument.title;
        document.body.dataset.Source = newDocument.body.dataset.Source;

        for (const component of this.components)
          component.reload(this.element, query);

        application.synchronizationCenter.initialize();
        distributeEvent(new NavigationEvent(uri));
      }
      else
        this.reloadDocument(newDocument);
    }
    else
      this.reloadDocument(newDocument);

    this.uri = uri;
  }

  reloadDocument(newDocument) {
    interactivityRegistration.detach(document.documentElement);
    document.documentElement.innerHTML = newDocument.documentElement.innerHTML;
    interactivityRegistration.attach(document.documentElement);

    application.initialize();
  }

  async loadTemplate() {
    const response = await fetch(application.resolve("Template"));

    if (!response.ok) {
      console.error("Failed to fetch offline template.");
      return false;
    }

    const templateText = await response.text();
    const templateDocument = new DOMParser().parseFromString(templateText, "text/html");

    this.reloadDocument(templateDocument);
  }

  async loadOnlinePage(uri, pushState, response) {
    if (pushState) {
      const pushUri = new URL(response.url);
      pushUri.hash = new URL(uri).hash;

      window.history.pushState(
        { url: pushUri.href },
        null,
        pushUri.href
      );
    }

    const text = await response.text();
    this.mode = PageMode.Online;
    this.handleDocument(uri, text);
  }

  isHtmlResponse(response) {
    return response.headers.get("Content-Type")?.includes("text/html") ?? false;
  }

  async loadOfflinePage(uri, pushState) {
    let result = false;

    if (application.mode === ApplicationMode.App) {
      const entity = await application.entityStore.get(uri);
      const template = await application.entityStore.getContents(uri);

      if (entity && template && entity.Current !== null) {
        this.mode = PageMode.Offline;

        if (document.body.dataset.Source !== "Template")
          await this.loadTemplate();

        const parser = new DOMParser();
        const contents = parser.parseFromString(template.Contents, "text/html");

        const title = document.createElement("h1");
        title.append(entity.Title);

        const container = document.createElement("div");
        container.appendChild(title);
        container.appendChild(contents.body.firstChild);

        const main = document.createElement("main");
        main.appendChild(container);

        const breadcrumbs = this.breadcrumbs.element.cloneNode(true);
        breadcrumbs.firstChild.innerHTML = "";

        const prefix = document.createElement("li");
        prefix.appendChild(document.createElement("span"));
        prefix.firstChild.appendChild(document.createElement("span"));

        const category = document.createElement("li");
        category.appendChild(document.createElement("span"));
        category.firstChild.innerText = entity.Category;

        const link = document.createElement("a");
        link.href = entity.Uri;
        link.innerText = entity.Title + " - " + entity.Details;

        const item = document.createElement("li");
        item.appendChild(document.createElement("span"));
        item.firstChild.appendChild(link);

        const list = document.createElement("ol");
        list.appendChild(prefix);
        list.appendChild(category);
        list.appendChild(item);

        breadcrumbs.firstChild.appendChild(list);

        this.contents.replace(this.element, main);
        this.breadcrumbs.replace(this.element, breadcrumbs);

        this.uri = uri;
        this.load(entity.Current);
        this.version = entity.Version;
        this.persisted = entity.Persisted;
        this.current = entity.Current;

        document.title = entity.Title;

        if (pushState)
          window.history.pushState(
            { url: uri },
            null,
            uri
          );

        result = true;
      }
    }

    return result;
  }

  async persistThumbnail(file, thumbnail) {
    const result = await application.fileStore.addThumbnail(file, thumbnail);

    if (this.uri) {
      const entity = await application.entityStore.get(this.uri);

      if (entity) {
        await application.entityStore.addThumbnail({
          Entity: entity.Uri,
          Id: file.id
        });
      }
    }

    return result;
  }

  async retrieveThumbnail(id) {
    const entity = await application.entityStore.get(this.uri);

    if (entity) {
      let file = await application.fileStore.getThumbnailByUuid(id);

      if (!file && /^[0-9]+$/.test(id))
        file = await application.fileStore.getThumbnail(parseInt(id));

      return file;
    }
    else
      return null;
  }

  async persistOfflinePage() {
    if (this.mode === PageMode.Offline) {
      console.info("Persisting offline page: " + this.uri);
      const entity = await application.entityStore.get(this.uri);
      const contents = await application.entityStore.getContents(this.uri);

      if (entity !== undefined && contents && entity.Current !== null) {
        const newValue = this.save();

        const saved = JSON.stringify(entity.Current);
        const server = JSON.stringify(entity.Persisted);
        const current = JSON.stringify(newValue);
        const loaded = JSON.stringify(this.persisted);

        if (current === saved) {
          console.info("No changes for: " + this.uri);
        }
        else {
          if (server === loaded || server === saved) {
            entity.Current = newValue;
            entity.State = SynchronizationState.Modified;
            entity.Attempts = 0;
            entity.NextAttempt_Utc = null;
            entity.Error = null;

            this.current = newValue;
            this.persisted = entity.Persisted;
            this.version = entity.Version;

            console.info("Offline page modified:", this.uri);
          }
          else {
            entity.Current = newValue;
            entity.Persisted = this.persisted;
            entity.Version = this.version;
            entity.State = SynchronizationState.Conflict;
            entity.Attempts = 0;
            entity.NextAttempt_Utc = null;
            entity.Error = null;

            this.current = newValue;

            console.warn("Conflict found: " + this.uri);
          }
        }

        await application.entityStore.update(entity);
        application.synchronizationCenter.reload();
      }
      else
        console.info("Entity not found: " + this.uri);
    }
  }

  load(value) {
    const components = interactivityRegistration.getComponents(this.contents.element);

    for (const component of components) {
      if (component.name && value[component.name])
        component.load(value[component.name]);
    }
  }

  save() {
    const components = interactivityRegistration.getComponents(this.contents.element);
    const result = {};

    for (const component of components) {
      if (component.mode === ControlMode.edit && component.name)
        result[component.name] = component.save();
    }

    return result;
  }

  fetch(uri) {
    if (application.online)
      return fetch(
        uri,
        {
          method: "GET",
          headers: {
            "Accept": "text/html"
          }
        }
      );
    else
      return Promise.reject(new Error("The application is offline"));
  }

  async canNavigate(uri) {
    for (const guard of this.guards) {
      const result = await guard.canNavigate(uri);

      if (!result)
        return false;
    }

    return true;
  }

  async navigate(uri) {
    let result = false;
    const currentUri = new URL(window.location.href);
    const navigationUri = new URL(uri);

    if (!this.loadingIndicator.getStatus()) {
      if (await this.canNavigate(uri)) {
        if ((navigationUri.pathname === currentUri.pathname) && (navigationUri.hash.length > 0 || navigationUri.search.length > 0))
          window.location = navigationUri;
        else {
          await this.loadPage(uri, true);
          result = true;
        }
      }
    }

    return result;
  }

  setStatus(loading) {
    this.loading.setStatus(loading);
    this.loadingIndicator.setStatus(loading);
  }

  async reload(uri) {
    await this.loadPage(uri, false);
  }

  async loadPage(uri, pushState) {
    this.setStatus(true);

    try {
      let loaded = await this.loadOfflinePage(uri, pushState);

      if (!loaded) {
        const response = await this.fetch(uri);

        if (this.isHtmlResponse(response)) {
          await this.loadOnlinePage(uri, pushState, response);
          loaded = true;
        }
        else
          window.location = uri;
      }

      if (loaded) {
        const navigationUri = new URL(uri);

        if (navigationUri.hash.length > 0)
          window.location.hash = navigationUri.hash;
      }
    }
    catch (error) {
      application.toastBox.addMessage(new ToastMessage(this.labels.FailedToLoadPage, "Warning"));
      console.error(error);
    }
    finally {
      this.setStatus(false);
    }
  }

  reloadRequired(newDocument) {
    if (newDocument.head.childNodes.length === document.head.childNodes.length) {
      for (let index = 0; index < newDocument.head.childNodes.length; index++) {
        const left = newDocument.head.childNodes[index];
        const right = document.head.childNodes[index];

        if (left.tagName !== "TITLE" && !left.isEqualNode(right)) {
          return true;
        }
      }

      return false;
    }
    else
      return true;
  }

  loadSupported() {
    return !this.components.some((component) => component.element === null);
  }

  getPopUps() {
    return new DomQuery(this.contents.element).getChildren(WithClass("PopUp"));
  }
}
